// V-01 新增家用面板（docs/panel/05-ui-ux.md V-01；SD §2.3.1 / §2.3a）：
// 輸入面板上的短碼（或貼上加入網址）→ 認領 → 顯示比對碼 →（有舊面板時問「這是新面板，還是取代舊的？」；
// 重新加入那台預設選中，SD §2.3a 主人指定取代）→ 取名（取代時預帶舊名）→ 核准 →
// V-22 快速設定（有給 devices 時：門鈴預設全勾 / 攝影機位置與插播 / 夜間，按完成才寫入；沒給就只預填門鈴）。
// 網頁沒有相機掃描，跟既有新增裝置一樣用文字輸入。
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';
import { formatRelativeUs } from '../../lib/format';
import type { DeviceSummary } from './deviceModel';
import {
  approvePanel, claimPanel, parsePanelJoinInput, prefillPanelDoorbells, rejectPanel, replaceCandidatesOf,
  type PanelClaim, type PanelReplaceCandidate,
} from './panels';
import { PanelQuickSetup } from './PanelP4Cards';

interface Props {
  doorbellIds: string[];
  /** 帳號內裝置（V-22 快速設定用；沒給就只預填門鈴） */
  devices?: DeviceSummary[];
  onClose?: () => void;
  onJoined?: (panelDeviceId: string) => void;
  /** 從新增裝置的通用輸入轉過來的面板加入網址：帶入並直接認領 */
  initialCode?: string;
}

type Step =
  | { kind: 'input' }
  | { kind: 'claimed'; claim: PanelClaim }
  | { kind: 'quick'; panelDeviceId: string }
  | { kind: 'done'; panelDeviceId: string };

export function panelJoinErrorKey(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'INVALID_OR_EXPIRED') return 'panels.errExpired';
    if (e.code === 'ALREADY_CLAIMED') return 'panels.errAlreadyClaimed';
    if (e.code === 'REPLACE_NOT_ALLOWED') return 'panels.errReplaceNotAllowed';
  }
  return 'panels.errGeneric';
}

export function PanelJoinPanel({ doorbellIds, devices, onClose, onJoined, initialCode }: Props) {
  const { t } = useTranslation();
  const [input, setInput] = useState(initialCode ?? '');
  const [step, setStep] = useState<Step>({ kind: 'input' });
  const [name, setName] = useState(() => t('panels.defaultName'));
  /** 'new' = 新面板；其他 = 要取代的 panel device id */
  const [choice, setChoice] = useState<string>('new');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const claim = async (raw = input) => {
    setError(null);
    const parsed = parsePanelJoinInput(raw);
    if (!parsed) {
      setError(t('panels.errNotPanelCode'));
      return;
    }
    setBusy(true);
    try {
      const c = await claimPanel(parsed);
      const suggested = replaceCandidatesOf(c).find((x) => x.is_rejoin);
      setChoice(suggested?.panel_device_id ?? 'new');
      setName(suggested?.name || t('panels.defaultName'));
      setStep({ kind: 'claimed', claim: c });
    } catch (e) {
      setError(t(panelJoinErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  // 帶入的碼直接認領一次（StrictMode 下 effect 跑兩次 → ref 擋）
  const autoClaimed = useRef(false);
  useEffect(() => {
    if (!initialCode || autoClaimed.current) return;
    autoClaimed.current = true;
    void claim(initialCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  const pick = (c: PanelClaim, id: string) => {
    setChoice(id);
    const target = replaceCandidatesOf(c).find((x) => x.panel_device_id === id);
    setName(target?.name || t('panels.defaultName'));
  };

  const approve = async (c: PanelClaim) => {
    setError(null);
    setBusy(true);
    const replacing = choice !== 'new';
    try {
      const opts = replacing ? { replaceOf: choice } : c.rejoin_candidate ? { keep: false } : {};
      const r = await approvePanel(c.activation_id, name.trim() || t('panels.defaultName'), opts);
      // 重新加入 / 取代且保留設定時不動設定
      if (replacing && (r.rejoined || r.replaced)) {
        setStep({ kind: 'done', panelDeviceId: r.panel_device_id });
        onJoined?.(r.panel_device_id);
      } else if (devices) {
        setStep({ kind: 'quick', panelDeviceId: r.panel_device_id });   // V-22：按完成才寫入
      } else {
        if (doorbellIds.length > 0) await prefillPanelDoorbells(r.panel_device_id, doorbellIds).catch(() => undefined);
        setStep({ kind: 'done', panelDeviceId: r.panel_device_id });
        onJoined?.(r.panel_device_id);
      }
    } catch (e) {
      setError(t(panelJoinErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  const reject = async (c: PanelClaim) => {
    setBusy(true);
    await rejectPanel(c.activation_id).catch(() => undefined);
    setBusy(false);
    setStep({ kind: 'input' });
    setInput('');
  };

  return (
    <div className="card dv-add-panel" data-testid="panel-join-panel">
      <div className="dv-pairing-head">
        <div>
          <h2>{t('panels.addTitle')}</h2>
          <p className="text-secondary">{t('panels.addInstructions')}</p>
        </div>
        {onClose && (
          <div className="dv-pairing-actions">
            <button className="btn-ghost" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>

      {step.kind === 'input' && (
        <>
          <label className="dv-pairing-input">
            <span>{t('panels.inputLabel')}</span>
            <input value={input} spellCheck={false} autoCapitalize="characters" placeholder="K7Q4-M2XD"
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void claim(); }} />
          </label>
          <div className="dv-pairing-actions">
            <button className="btn-primary" onClick={() => void claim()} disabled={!input.trim() || busy}>
              {t('panels.next')}
            </button>
          </div>
        </>
      )}

      {step.kind === 'claimed' && (
        <section className="dv-pairing-summary" aria-live="polite">
          <h3>{step.claim.rejoin_candidate ? t('panels.rejoinAsk', { name: step.claim.rejoin_candidate.name })
            : replaceCandidatesOf(step.claim).length > 0 ? t('panels.replaceAsk') : t('panels.confirmAdd')}</h3>
          <p className="dv-panel-match" data-testid="panel-match-code">{t('panels.matchCode', { code: step.claim.match_code })}</p>
          {step.claim.rejoin_candidate?.reason === 'refresh_reused' && <p className="error-text">{t('panels.rejoinCloned')}</p>}
          {replaceCandidatesOf(step.claim).length > 0 && (
            <ReplaceChoice claim={step.claim} choice={choice} onPick={(id) => pick(step.claim, id)} />
          )}
          <label className="dv-pairing-input">
            <span>{t('panels.nameLabel')}</span>
            <input value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />
          </label>
          <div className="dv-pairing-actions">
            <button className="btn-primary" onClick={() => void approve(step.claim)} disabled={busy}>
              {t('panels.add')}
            </button>
            <button className="btn-ghost" onClick={() => void reject(step.claim)} disabled={busy}>
              {t('panels.reject')}
            </button>
          </div>
        </section>
      )}

      {step.kind === 'quick' && devices && (
        <PanelQuickSetup panelId={step.panelDeviceId} devices={devices}
          onDone={() => { const id = step.panelDeviceId; setStep({ kind: 'done', panelDeviceId: id }); onJoined?.(id); }} />
      )}

      {step.kind === 'done' && (
        <section className="dv-pairing-summary" aria-live="polite" data-testid="panel-join-done">
          <h3>{t('panels.done')}</h3>
          <p className="text-secondary">{t('panels.diagHint')}</p>
        </section>
      )}

      {error && <p className="error-text dv-pairing-message">{error}</p>}
    </div>
  );
}

/** 「這是新面板，還是取代舊的？」：［新面板］＋每一台可取代的面板（名稱、最後上線、照片數） */
function ReplaceChoice({ claim, choice, onPick }: { claim: PanelClaim; choice: string; onPick: (id: string) => void }) {
  const { t } = useTranslation();
  const cands = replaceCandidatesOf(claim);
  const showMeta = !!claim.replace_candidates; // 舊雲端只有 rejoin_candidate，沒有上線 / 照片資訊
  const target = cands.find((x) => x.panel_device_id === choice);
  const meta = (x: PanelReplaceCandidate) => {
    const seen = x.online ? t('panels.replaceOnline')
      : x.last_contact_at ? t('panels.replaceLastSeen', { time: formatRelativeUs(x.last_contact_at) }) : t('panels.replaceNever');
    return `${seen}・${t('panels.photoCount', { count: x.photo_count })}`;
  };
  return (
    <>
      <fieldset className="dv-method-select" data-testid="panel-replace-choice">
        <label>
          <input type="radio" name="panel-replace" checked={choice === 'new'} onChange={() => onPick('new')} />
          <span>{t('panels.replaceNew')}</span>
        </label>
        {cands.map((x) => (
          <label key={x.panel_device_id}>
            <input type="radio" name="panel-replace" checked={choice === x.panel_device_id} onChange={() => onPick(x.panel_device_id)} />
            <span>
              {t('panels.replaceOption', { name: x.name })}
              {x.is_rejoin && <> <strong>{t('panels.replaceSuggested')}</strong></>}
              {showMeta && <><br /><small className="text-secondary">{meta(x)}</small></>}
            </span>
          </label>
        ))}
      </fieldset>
      {target && <p className="text-secondary" data-testid="panel-replace-hint">{t('panels.replaceHint', { name: target.name })}</p>}
      {target?.online && <p className="error-text">{t('panels.replaceOnlineWarn', { name: target.name })}</p>}
    </>
  );
}
